/**
 * useNotificationPolling Hook (AC-541)
 *
 * A custom hook that manages notification polling at configurable intervals.
 * Automatically starts/stops polling based on authentication state and
 * visibility.
 *
 * Related Tickets:
 * - AC-532 (Epic: In-App Notification System)
 * - AC-541 (Polling hook - THIS TICKET)
 */

import { useEffect, useCallback, useRef } from 'react';
import { useNotificationStore } from '../store/notificationStore';
import { useAuthStore } from '../store';

// Default polling interval: 30 seconds
const DEFAULT_POLL_INTERVAL = 30000;

interface UseNotificationPollingOptions {
  /**
   * Polling interval in milliseconds.
   * Default: 30000 (30 seconds)
   */
  interval?: number;

  /**
   * Whether polling is enabled.
   * Default: true
   */
  enabled?: boolean;

  /**
   * Whether to pause polling when tab is not visible.
   * Default: true
   */
  pauseOnHidden?: boolean;
}

/**
 * Hook to manage notification polling.
 *
 * Usage:
 * ```tsx
 * // In App.tsx or a top-level component
 * useNotificationPolling({ interval: 30000 });
 * ```
 */
export function useNotificationPolling(options: UseNotificationPollingOptions = {}) {
  const {
    interval = DEFAULT_POLL_INTERVAL,
    enabled = true,
    pauseOnHidden = true,
  } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pollNotifications = useNotificationStore((state) => state.pollNotifications);
  const fetchNotifications = useNotificationStore((state) => state.fetchNotifications);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (!document.hidden || !pauseOnHidden) {
        pollNotifications();
      }
    }, interval);
  }, [interval, pauseOnHidden, pollNotifications]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Manual poll trigger
  const triggerPoll = useCallback(() => {
    pollNotifications();
  }, [pollNotifications]);

  // Effect to manage polling lifecycle
  useEffect(() => {
    // Only poll when authenticated and enabled
    if (!isAuthenticated || !enabled) {
      stopPolling();
      return;
    }

    // Initial fetch when first enabled
    fetchNotifications();

    // Start polling
    startPolling();

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden && pauseOnHidden) {
        stopPolling();
      } else if (!document.hidden && isAuthenticated && enabled) {
        // Poll immediately when becoming visible
        pollNotifications();
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isAuthenticated,
    enabled,
    pauseOnHidden,
    startPolling,
    stopPolling,
    fetchNotifications,
    pollNotifications,
  ]);

  return {
    triggerPoll,
    startPolling,
    stopPolling,
  };
}

export default useNotificationPolling;
